<script setup>
import {computed, onMounted, ref} from "vue";
import MediaBankEntry from "./util/media-bank-entry.vue";

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        require: true
    },
    content: {
        type: Object,
        required: true,
    },
});

const version = ref(1)

const hasIcon = computed(() => {
    return props.content.icon_type !== 'none';
})

const iconType = computed(() => {
    if (props.content.icon_type === 'image') {
        if (props.content.icon_source_type === 'mediaLibrary') {
            return 'media-library'
        }
    }

    if (props.content.icon_type === 'text') {
        return 'text'
    }
})


const alignmentClass = computed(() => {
    if (props.content?.alignment === 'left') {
        return 'justify-start'
    }
    if (props.content?.alignment === 'center') {
        return 'justify-center'
    }
    if (props.content?.alignment === 'right') {
        return 'justify-end'
    }
    if (props.content?.alignment === 'fullwidth') {
        return 'justify-stretch'
    }

    return 'justify-center'
})

const buttonClass = computed( () => {
    let classes = [];

    if (props.content.alignment === 'fullwidth') {
        classes.push('block w-full');
    } else {
        classes.push('inline-block');
    }

    if (props.content.color_mode === 'siteAppearance') {
        classes.push('border-2 border-wlPrimary bg-wlPrimary text-wlPrimaryContrast');
    }

    classes.push('text-'+props.content.text_alignment)

    if (props.content.rounding === 'siteAppearance') {
        classes.push('rounded-lg px-2');
    } else if (props.content.rounding === 'none') {
        classes.push('rounded-none px-2');
    } else if (props.content.rounding === 'slight') {
        classes.push('rounded-lg px-2');
    } else if (props.content.rounding === 'medium') {
        classes.push('rounded-3xl px-4');
    } else if (props.content.rounding === 'very') {
        classes.push('rounded-5xl px-4');
    } else {
        classes.push('rounded-lg px-4');
    }

    return classes.join(' ')
})

const buttonContentClass = computed(() => {
    let classes = [];

    if (props.content.icon_type !== 'none') {
        classes.push('flex items-center')
        if (props.content.icon_position === 'left') {
            classes.push('pr-2')
        }
        if (props.content.icon_position === 'top') {
            classes.push('flex-col items-center')
        }
        if (props.content.icon_position === 'right') {
            classes.push('flex-row-reverse justify-between')
        }
    }

    return classes.join(' ');
})

const buttonTextClass = computed(() => {
    const classes = [];

    if (props.content.icon_position === 'top') {
        classes.push('text-center')
    }

    return classes.join(' ')
})

const buttonLabelTextClass = computed(() => {
    const classes = [];

    if (props.content.font_style === 'bold') {
        classes.push('font-bold')
    }

    return classes.join(' ')
})

const iconClass = computed(() => {
    let classes = [];
    if (props.content.icon_type !== 'none') {
        if (props.content.icon_position === 'left') {
            classes.push('mr-2');
        }
        if (props.content.icon_position === 'top') {

        }
        if (props.content.icon_position === 'right') {
            classes.push('ml-3');
        }

        classes.push('icon-size-'+props.content.font_size)
    }

    return classes.join(' ');
})

const buttonStyle = computed( () => {
    const style = {};
    if (props.content.color_mode === 'custom') {
        if (props.content.cssBackgroundColor) {
            style.border = '2px solid ' + props.content.cssBackgroundColor;
            style.backgroundColor = props.content.cssBackgroundColor;
        } else {
            style.border = '2px solid #efefef';
            style.backgroundColor = '#efefef';
        }
        if (props.content.cssColor) {
            style.color = props.content.cssColor;
        } else {
            style.color = '#3f3f3f';
        }
    }

    if (props.content.min_width) {
        style.minWidth = props.content.min_width + props.content.min_width_unit
    }

    return style;
})


onMounted(() => {
    if (props.content?.version) {
        version.value = props.content.version;
    }
})

</script>
<template>
    <div>
        <div
            v-if="version === 1"
            class="flex justify-center"
        >
            <button-primary>
                {{ content.label }}
            </button-primary>
        </div>

        <div v-else
             class="flex mx-auto mb-6 max-w-full"
             :class="alignmentClass"
        >
            <div
                :class="buttonClass"
                :style="buttonStyle"
                class="flex cursor-pointer py-1"
            >
                <div :class="buttonContentClass" class="w-full">
                    <span
                        v-if="hasIcon"
                        :class="iconClass"
                    >
                        <MediaBankEntry
                            v-if="iconType === 'media-library'"
                            :container-ref="containerRef"
                            :block-uuid="uuid"
                            :block-content="props.content"
                            :media-bank-entry-id="props.content.media_bank_entry_id"
                            property-name="media_bank_entry_id"
                            class="aspect-square object-contain text-2xl w-8 h-8"
                        ></MediaBankEntry>

                        <span
                            v-if="iconType === 'text'"
                            class="text-2xl button-unified-icon-type-text"
                        >
                            {{ props.content.icon_text_content }}
                        </span>
                    </span >

                    <div :class="buttonTextClass">
                        <div class="text-sm" :class="buttonLabelTextClass">
                            {{ content.label }}
                        </div>
                        <div class="text-xs opacity-80">
                            {{ content.byline }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
